import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  AlertCircle,
  Trash2,
  Check,
  CheckCircle2,
  MessageSquare,
  List,
  GitBranch,
  X,
  Pencil,
} from "lucide-react";
import { ProjectService } from "../services/projectService";
import { TemplateCategoryService } from "../services/templateCategoryService";
import { supabase } from "../lib/supabase";
import { formatDate } from "../utils/dateFormatting";
import { useDebounce } from "../hooks/useDebounce";
import HoldProjectModal from "./HoldProjectModal";
import FileUpload from "./FileUpload/FileUpload";
import BaseProjectSelect from "./BaseProjectSelect";
import {
  Modal,
  ModalBody,
  ModalFooter,
  Input,
  Button,
  Dropdown,
  ConfirmDialog,
} from "./ui";
import { formatJobRevNumber } from "../utils/projectFormatting";
import { useTrackModal } from "../hooks/useTrackModal";

const ProjectSetupModal = ({
  project,
  isOpen,
  onClose,
  onSave,
  onDelete,
  clients,
  contacts,
}) => {
  useTrackModal("ProjectSetupModal", isOpen);
  const [formData, setFormData] = useState({
    projectName: "",
    jobNumber: "",
    rev: 0,
    isRevision: false,
    clientId: "",
    contactId: "",
    usState: "",
    approximateAcreage: "",
    templateFilter: [],
    additionalRequests: [],
    pdfsReceived: false,
    cadFilesReceived: false,
    projectSetupComplete: false,
    fileAttachments: [],
  });

  const [loading, setLoading] = useState(false);
  const [slackLoading, setSlackLoading] = useState(false);
  const [subitemsLoading, setSubitemsLoading] = useState(false);
  const [completeSetupLoading, setCompleteSetupLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState("saved"); // 'saving', 'saved', 'error'
  const [lastSavedData, setLastSavedData] = useState(null);
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRevisionConfirm, setShowRevisionConfirm] = useState(false);
  const [showCategoryConfirm, setShowCategoryConfirm] = useState(false);
  const [pendingCategoryChange, setPendingCategoryChange] = useState(null);
  const [categoryConfirmLoading, setCategoryConfirmLoading] = useState(false);

  // New category-based state
  const [categories, setCategories] = useState([]);
  const [projectCategoryIds, setProjectCategoryIds] = useState([]);
  const [deliverableType, setDeliverableType] = useState("surface"); // 'surface' or 'linework'
  const [projectTypeId, setProjectTypeId] = useState(""); // Category ID for project type
  const [addOns, setAddOns] = useState({
    hasPond: false,
    elevatedUtilities: false,
    advancedUtilityModel: false,
    hasErosionSurface: false,
    hasCenterlineProfiles: false,
  });

  // Revision handling state
  const [revisionMode, setRevisionMode] = useState("none"); // 'none', 'form_marked', 'converting'
  const [selectedBaseProjectId, setSelectedBaseProjectId] = useState(null);
  const [selectedBaseProject, setSelectedBaseProject] = useState(null); // Full base project data
  const [showNotRevisionConfirm, setShowNotRevisionConfirm] = useState(false);
  const [showConvertToRevisionConfirm, setShowConvertToRevisionConfirm] = useState(false);

  // File formats editing state
  const [isEditingFileFormats, setIsEditingFileFormats] = useState(false);
  const [editedFileFormats, setEditedFileFormats] = useState([]);

  // Template filter options - actual values from database
  const templateFilterOptions = [
    "00-All Projects",
    "01-Advanced Utility Model",
    "01-Linework Design",
    "01-Surface Design",
    "02-Commercial",
    "02-Corridor",
    "02-Elevated Utilities",
    "02-Erosion Surface",
    "02-Landfill",
    "02-Multifamily",
    "02-Pond",
    "02-Solar",
    "02-Sports Field",
    "02-Subdivision",
    "03-Leica",
    "03-Topcon",
    "03-Trimble",
    "International Feet",
  ];

  // US State options - 2-letter abbreviations
  const usStateOptions = [
    { value: "AL", label: "AL - Alabama" },
    { value: "AK", label: "AK - Alaska" },
    { value: "AZ", label: "AZ - Arizona" },
    { value: "AR", label: "AR - Arkansas" },
    { value: "CA", label: "CA - California" },
    { value: "CO", label: "CO - Colorado" },
    { value: "CT", label: "CT - Connecticut" },
    { value: "DE", label: "DE - Delaware" },
    { value: "DC", label: "DC - District of Columbia" },
    { value: "FL", label: "FL - Florida" },
    { value: "GA", label: "GA - Georgia" },
    { value: "HI", label: "HI - Hawaii" },
    { value: "ID", label: "ID - Idaho" },
    { value: "IL", label: "IL - Illinois" },
    { value: "IN", label: "IN - Indiana" },
    { value: "IA", label: "IA - Iowa" },
    { value: "KS", label: "KS - Kansas" },
    { value: "KY", label: "KY - Kentucky" },
    { value: "LA", label: "LA - Louisiana" },
    { value: "ME", label: "ME - Maine" },
    { value: "MD", label: "MD - Maryland" },
    { value: "MA", label: "MA - Massachusetts" },
    { value: "MI", label: "MI - Michigan" },
    { value: "MN", label: "MN - Minnesota" },
    { value: "MS", label: "MS - Mississippi" },
    { value: "MO", label: "MO - Missouri" },
    { value: "MT", label: "MT - Montana" },
    { value: "NE", label: "NE - Nebraska" },
    { value: "NV", label: "NV - Nevada" },
    { value: "NH", label: "NH - New Hampshire" },
    { value: "NJ", label: "NJ - New Jersey" },
    { value: "NM", label: "NM - New Mexico" },
    { value: "NY", label: "NY - New York" },
    { value: "NC", label: "NC - North Carolina" },
    { value: "ND", label: "ND - North Dakota" },
    { value: "OH", label: "OH - Ohio" },
    { value: "OK", label: "OK - Oklahoma" },
    { value: "OR", label: "OR - Oregon" },
    { value: "PA", label: "PA - Pennsylvania" },
    { value: "RI", label: "RI - Rhode Island" },
    { value: "SC", label: "SC - South Carolina" },
    { value: "SD", label: "SD - South Dakota" },
    { value: "TN", label: "TN - Tennessee" },
    { value: "TX", label: "TX - Texas" },
    { value: "UT", label: "UT - Utah" },
    { value: "VT", label: "VT - Vermont" },
    { value: "VA", label: "VA - Virginia" },
    { value: "WA", label: "WA - Washington" },
    { value: "WV", label: "WV - West Virginia" },
    { value: "WI", label: "WI - Wisconsin" },
    { value: "WY", label: "WY - Wyoming" },
  ];

  // Master templates in server order
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const MASTER_TEMPLATES = useMemo(() => templateFilterOptions, []);

  const sortByMaster = useCallback(
    (arr) => {
      const pos = new Map(MASTER_TEMPLATES.map((t, i) => [t, i]));
      return [...arr].sort((a, b) => (pos.get(a) ?? 1e9) - (pos.get(b) ?? 1e9));
    },
    [MASTER_TEMPLATES],
  );

  // Template filter value mapping - maps database values to UI option values
  const createTemplateFilterMapping = () => {
    const mapping = {
      // Database value -> UI option value
      Trimble: "03-Trimble",
      "Linework Only": "01-Linework Design",
      "Linework Design": "01-Linework Design",
      "Surface Design": "01-Surface Design",
      "Advanced Utility Model": "01-Advanced Utility Model",
      Commercial: "02-Commercial",
      Corridor: "02-Corridor",
      "Elevated Utilities": "02-Elevated Utilities",
      "Erosion Surface": "02-Erosion Surface",
      Solar: "02-Solar",
      Subdivision: "02-Subdivision",
      Leica: "03-Leica",
      Topcon: "03-Topcon",
      "All Projects": "00-All Projects",
    };

    // Create reverse mapping as well (UI -> database)
    const reverseMapping = {};
    Object.entries(mapping).forEach(([dbValue, uiValue]) => {
      reverseMapping[uiValue] = dbValue;
    });

    return { mapping, reverseMapping };
  };

  const { mapping: dbToUiMapping } = createTemplateFilterMapping();

  // Helper function to normalize database values to UI values
  const normalizeTemplateFilterValues = (dbValues) => {
    if (!Array.isArray(dbValues)) return [];
    return dbValues
      .map((dbValue) => dbToUiMapping[dbValue] || dbValue)
      .filter(Boolean);
  };

  // Filter contacts based on selected client
  const filteredContacts = useMemo(() => {
    if (!formData.clientId || !contacts) return [];
    const filtered = contacts.filter((contact) => {
      return (
        String(contact.clientId) === String(formData.clientId) ||
        String(contact.client_id) === String(formData.clientId)
      );
    });
    return filtered;
  }, [formData.clientId, contacts]);

  // Prepare dropdown options for clients
  const clientOptions = useMemo(() => {
    if (!clients) return [];
    return clients.map((client) => ({
      value: client.id,
      label: client.name,
    }));
  }, [clients]);

  // Prepare dropdown options for contacts
  const contactOptions = useMemo(() => {
    if (!filteredContacts) return [];
    return filteredContacts.map((contact) => ({
      value: contact.id,
      label: contact.name,
    }));
  }, [filteredContacts]);

  // Load categories from database
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await TemplateCategoryService.getCategories();
        setCategories(categoriesData);
      } catch (err) {
        console.error("Error loading categories:", err);
      }
    };
    loadCategories();
  }, []);

  // Load this project's assigned categories so the UI can reflect
  // trigger-managed flags (e.g. International Feet) without inventing
  // them from the legacy templateFilter column.
  useEffect(() => {
    if (!isOpen || !project?.id) {
      setProjectCategoryIds([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ids = await TemplateCategoryService.getProjectCategories(project.id);
        if (!cancelled) setProjectCategoryIds(ids);
      } catch (err) {
        console.error("Error loading project categories:", err);
        if (!cancelled) setProjectCategoryIds([]);
      }
    })();
    return () => { cancelled = true; };
  }, [project?.id, isOpen]);

  // Group categories by type for UI
  const categoriesGrouped = useMemo(() => {
    return {
      deliverable: categories.filter((c) => c.categoryType === "deliverable"),
      projectType: categories.filter((c) => c.categoryType === "project_type"),
      addOn: categories.filter((c) => c.categoryType === "add_on"),
      fileFormat: categories.filter((c) => c.categoryType === "file_format"),
      unitSystem: categories.filter((c) => c.categoryType === "unit_system"),
    };
  }, [categories]);

  // Derived: does this project have any unit_system category assigned?
  // Source of truth is project_categories (set by DB trigger), not templateFilter.
  const hasInternationalFeet = useMemo(() => {
    if (!categories.length || !projectCategoryIds.length) return false;
    const unitSystemIds = new Set(
      categories.filter((c) => c.categoryType === "unit_system").map((c) => c.id)
    );
    return projectCategoryIds.some((id) => unitSystemIds.has(id));
  }, [categories, projectCategoryIds]);

  // Project type dropdown options
  const projectTypeOptions = useMemo(() => {
    // Exclude "Erosion Surface" - those projects are auto-created from file uploads
    const filteredTypes = categoriesGrouped.projectType.filter(
      (cat) => cat.name !== "Erosion Surface"
    );
    return [
      { value: "", label: "Select project type..." },
      ...filteredTypes.map((cat) => ({
        value: cat.id.toString(),
        // Display "Pond Only" instead of "Pond" for clarity
        label: cat.name === "Pond" ? "Pond Only" : cat.displayName,
      })),
    ];
  }, [categoriesGrouped.projectType]);

  // Determine if acreage is required based on project type
  // NOT required for: Sports Field, Corridor, Landfill, Pond, Linework Only, or Revisions
  const acreageRequired = useMemo(() => {
    // Revisions never need acreage - check multiple indicators:
    // 1. rev > 0 (already linked revision)
    // 2. project.isRevision (form marked as revision, rev may still be 0)
    // 3. revisionMode is active (user is setting up a revision)
    const revNum = parseInt(project?.rev, 10) || 0;
    const isRevision = revNum > 0 ||
      project?.isRevision ||
      revisionMode === "form_marked" ||
      revisionMode === "converting";

    if (isRevision) {
      return false;
    }
    if (deliverableType === "linework") {
      return false;
    }
    if (!projectTypeId) {
      return false; // Can't require until type is selected
    }

    const selectedType = categoriesGrouped.projectType.find(
      (c) => c.id.toString() === projectTypeId
    );
    if (!selectedType) {
      return false;
    }

    const noAcreageTypes = ["Sports Field", "Corridor", "Landfill", "Pond"];
    return !noAcreageTypes.includes(selectedType.name);
  }, [project?.rev, project?.isRevision, revisionMode, deliverableType, projectTypeId, categoriesGrouped.projectType]);

  useEffect(() => {
    if (project && isOpen) {
      // Normalize template filter values from database to UI format
      const initialTemplateFilter = normalizeTemplateFilterValues(
        Array.isArray(project.templateFilter) ? project.templateFilter : [],
      );

      // Derive isRevision from rev - rev > 0 and rev !== 999 (erosion) means it's a revision
      const revNum = parseInt(project.rev, 10) || 0;
      const derivedIsRevision = revNum > 0 && revNum !== 999;

      setFormData({
        projectName: project.projectName || "",
        jobNumber: project.jobNumber || "",
        rev: project.rev || 0,
        isRevision: derivedIsRevision,
        clientId: project.clientId || "",
        contactId: project.contactId || "",
        usState: project.usState || "",
        approximateAcreage: project.approximateAcreage || "",
        templateFilter: initialTemplateFilter,
        additionalRequests: Array.isArray(project.additionalRequests)
          ? project.additionalRequests
          : [],
        pdfsReceived: project.pdfsReceived || false,
        cadFilesReceived: project.cadFilesReceived || false,
        projectSetupComplete: project.projectSetupComplete || false,
        fileAttachments: project.file_attachments || [],
      });
      setError(null);

      // Save initial data for comparison
      setLastSavedData({
        projectName: project.projectName || "",
        jobNumber: project.jobNumber || "",
        rev: project.rev || 0,
        isRevision: derivedIsRevision,
        clientId: project.clientId || "",
        contactId: project.contactId || "",
        usState: project.usState || "",
        approximateAcreage: project.approximateAcreage || "",
        templateFilter: initialTemplateFilter,
        additionalRequests: Array.isArray(project.additionalRequests)
          ? project.additionalRequests
          : [],
        pdfsReceived: project.pdfsReceived || false,
        cadFilesReceived: project.cadFilesReceived || false,
        projectSetupComplete: project.projectSetupComplete || false,
        fileAttachments: project.file_attachments || [],
      });

      // Initialize revision state based on project data
      // Check if customer marked this as revision (is_revision=true) but rev is still 0
      // This indicates form submission marked as revision but not yet linked to base project
      if (project.isRevision && revNum === 0 && !project.parentProjectId) {
        setRevisionMode("form_marked");
        setSelectedBaseProjectId(null);
        setSelectedBaseProject(null);
      } else if (project.parentProjectId) {
        // Already linked to base project
        setRevisionMode("form_marked");
        setSelectedBaseProjectId(project.parentProjectId);
      } else if (derivedIsRevision) {
        // Rev > 0 means it's already a linked revision (from job number matching)
        setRevisionMode("none"); // Already a revision, no action needed
        setSelectedBaseProjectId(null);
        setSelectedBaseProject(null);
      } else {
        // Not a revision
        setRevisionMode("none");
        setSelectedBaseProjectId(null);
        setSelectedBaseProject(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, isOpen]);

  // Initialize new category-based state from templateFilter when project loads
  useEffect(() => {
    if (!project || !isOpen || categories.length === 0) return;

    const templateFilter = Array.isArray(project.templateFilter)
      ? normalizeTemplateFilterValues(project.templateFilter)
      : [];

    // Determine deliverable type
    if (templateFilter.includes("01-Linework Design")) {
      setDeliverableType("linework");
    } else {
      setDeliverableType("surface");
    }

    // Determine project type from templateFilter
    const projectTypeMapping = {
      "02-Commercial": "Commercial",
      "02-Subdivision": "Subdivision",
      "02-Multifamily": "Multifamily",
      "02-Pond": "Pond",
      "02-Corridor": "Corridor",
      "02-Landfill": "Landfill",
      "02-Solar": "Solar",
      "02-Sports Field": "Sports Field",
      "02-Erosion Surface": "Erosion Surface",
    };

    let foundProjectTypeId = "";
    for (const [filterValue, categoryName] of Object.entries(projectTypeMapping)) {
      if (templateFilter.includes(filterValue)) {
        const category = categoriesGrouped.projectType.find(
          (c) => c.name === categoryName
        );
        if (category) {
          foundProjectTypeId = category.id.toString();
          break;
        }
      }
    }
    setProjectTypeId(foundProjectTypeId);

    // Determine add-ons
    // Check both templateFilter and additionalRequests from model request
    const additionalRequests = Array.isArray(project.additionalRequests)
      ? project.additionalRequests
      : [];

    // "Has Pond" is separate from "Pond" project type - it's when a non-pond project has pond work
    const hasPondAsAddOn = templateFilter.includes("02-Pond") && foundProjectTypeId !== "" &&
      categoriesGrouped.projectType.find(c => c.id.toString() === foundProjectTypeId)?.name !== "Pond";

    // Check for elevated utilities from templateFilter OR additionalRequests
    const hasElevatedUtilities = templateFilter.includes("02-Elevated Utilities") ||
      additionalRequests.includes("3D/Elevated Pipe");

    // Check for erosion surface from templateFilter OR additionalRequests
    const hasErosion = templateFilter.includes("02-Erosion Surface") ||
      additionalRequests.includes("Initial Erosion Surface");

    // Determine if project has centerline profiles
    // Default: true for Subdivision, always true for Corridor (UI won't show option)
    // Check project_categories to see if category 20 (Has Centerline Profiles) is assigned
    const projectTypeName = categoriesGrouped.projectType.find(c => c.id.toString() === foundProjectTypeId)?.name;
    const isSubdivision = projectTypeName === "Subdivision";
    const isCorridor = projectTypeName === "Corridor";
    // Default to true for Subdivision/Corridor unless we find it was explicitly disabled
    const hasCenterlineProfiles = isSubdivision || isCorridor;

    setAddOns({
      hasPond: hasPondAsAddOn,
      elevatedUtilities: hasElevatedUtilities,
      advancedUtilityModel: templateFilter.includes("01-Advanced Utility Model"),
      hasErosionSurface: hasErosion,
      hasCenterlineProfiles: hasCenterlineProfiles,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, isOpen, categories]);

  // Rebuild templateFilter from category selections
  // This syncs the new UI state to the legacy templateFilter array
  // Build array of category IDs from UI selections
  const buildCategoryIds = useCallback(() => {
    const categoryIds = new Set();

    // Always add "All Projects" (category ID 1)
    const allProjectsCat = categories.find(c => c.name === "All Projects");
    if (allProjectsCat) categoryIds.add(allProjectsCat.id);

    // Add deliverable type
    if (deliverableType === "linework") {
      const lineworkCat = categories.find(c => c.name === "Linework Design");
      if (lineworkCat) categoryIds.add(lineworkCat.id);
    } else {
      const surfaceCat = categories.find(c => c.name === "Surface Design");
      if (surfaceCat) categoryIds.add(surfaceCat.id);
    }

    // Add project type
    if (projectTypeId) {
      categoryIds.add(parseInt(projectTypeId, 10));
    }

    // Add add-ons
    if (addOns.hasPond) {
      const pondCat = categories.find(c => c.name === "Has Pond");
      if (pondCat) categoryIds.add(pondCat.id);
    }
    if (addOns.elevatedUtilities) {
      const elevatedCat = categories.find(c => c.name === "Elevated Utilities");
      if (elevatedCat) categoryIds.add(elevatedCat.id);
    }
    if (addOns.advancedUtilityModel) {
      const advancedCat = categories.find(c => c.name === "Advanced Utility Model");
      if (advancedCat) categoryIds.add(advancedCat.id);
    }
    if (addOns.hasErosionSurface) {
      const erosionCat = categories.find(c => c.name === "Erosion Surface");
      if (erosionCat) categoryIds.add(erosionCat.id);
    }
    if (addOns.hasCenterlineProfiles) {
      const profilesCat = categories.find(c => c.name === "Has Centerline Profiles");
      if (profilesCat) categoryIds.add(profilesCat.id);
    }
    // Corridor always gets centerline profiles (even if checkbox not shown)
    const selectedProjectType = categories.find(c => c.id === parseInt(projectTypeId, 10));
    if (selectedProjectType?.name === "Corridor") {
      const profilesCat = categories.find(c => c.name === "Has Centerline Profiles");
      if (profilesCat) categoryIds.add(profilesCat.id);
    }

    // Add file format categories based on project.fileFormatsNeeded
    if (project?.fileFormatsNeeded?.length > 0) {
      project.fileFormatsNeeded.forEach(format => {
        const formatCat = categories.find(c => c.name === format && c.categoryType === "file_format");
        if (formatCat) categoryIds.add(formatCat.id);
      });
    }

    // unit_system (International Feet) is intentionally NOT included here.
    // It is managed by the DB trigger auto_add_international_feet keyed off us_state,
    // and the service layer protects it from being clobbered on save.

    return Array.from(categoryIds);
  }, [deliverableType, projectTypeId, addOns, categories, project?.fileFormatsNeeded]);

  // Category IDs are now built on-demand in handleCompleteSetup
  // No need to sync to templateFilter anymore - we save directly to project_categories table

  // Debounced auto-save
  const debouncedFormData = useDebounce(formData, 1000);

  // Special debounced job number for immediate save when 4 digits entered
  const debouncedJobNumber = useDebounce(formData.jobNumber, 500);

  useEffect(() => {
    if (
      !project ||
      !isOpen ||
      !debouncedFormData ||
      autoSaveStatus === "saving"
    ) {
      return;
    }

    // Check if data has changed
    const hasChanged =
      JSON.stringify(debouncedFormData) !== JSON.stringify(lastSavedData);

    if (hasChanged) {
      autoSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFormData]);

  // Auto-sync: additional_requests → template_filter
  useEffect(() => {
    if (
      !formData.additionalRequests ||
      formData.additionalRequests.length === 0
    ) {
      return;
    }

    let templatesNeedingAdd = [];

    // Map additional requests to their corresponding templates
    if (formData.additionalRequests.includes("Initial Erosion Surface")) {
      if (!formData.templateFilter.includes("02-Erosion Surface")) {
        templatesNeedingAdd.push("02-Erosion Surface");
      }
    }

    if (formData.additionalRequests.includes("3D/Elevated Pipe")) {
      if (!formData.templateFilter.includes("02-Elevated Utilities")) {
        templatesNeedingAdd.push("02-Elevated Utilities");
      }
    }

    // Add the templates if needed
    if (templatesNeedingAdd.length > 0) {
      const updatedTemplates = sortByMaster([
        ...formData.templateFilter,
        ...templatesNeedingAdd,
      ]);
      handleInputChange("templateFilter", updatedTemplates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.additionalRequests]);

  // Auto-toggle: isRevision based on rev value
  useEffect(() => {
    const revValue = parseInt(formData.rev);

    // Auto-enable isRevision if rev is anything except 0, null, or 999
    if (revValue && revValue !== 0 && revValue !== 999) {
      if (!formData.isRevision) {
        handleInputChange("isRevision", true);
      }
    } else if (revValue === 0 || revValue === 999 || !revValue) {
      // Auto-disable isRevision if rev is 0, 999, or empty
      if (formData.isRevision) {
        handleInputChange("isRevision", false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.rev]);

  // ============================================================================
  // JOB NUMBER SYNC WITH REALTIME SUBSCRIPTION
  // ============================================================================
  // This handles auto-assignment of REV when job_number is entered/changed.
  // Uses Supabase realtime to listen for DB changes triggered by auto_link_revision.
  // ============================================================================

  // Track if we're currently saving to prevent re-processing our own updates
  const isSavingJobNumber = useRef(false);
  // Track the last job number we sent to DB to detect actual changes
  const lastSentJobNumber = useRef(null);

  // Realtime subscription for project changes (specifically rev and is_revision)
  useEffect(() => {
    if (!project?.id || !isOpen) return;

    console.log("[Realtime] Setting up subscription for project:", project.id);

    const channel = supabase
      .channel(`project-${project.id}-rev`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "projects",
          filter: `id=eq.${project.id}`,
        },
        (payload) => {
          console.log("[Realtime] Received update:", payload.new);

          // Only process if we're not currently in the middle of our own save
          // or if the rev/is_revision actually changed
          const newRev = payload.new.rev;
          const newIsRevision = payload.new.is_revision;
          const newJobNumber = payload.new.job_number;

          // Update form state with values from DB
          setFormData((prev) => {
            // Only update if values actually differ to prevent infinite loops
            if (prev.rev !== newRev || prev.isRevision !== newIsRevision || prev.jobNumber !== newJobNumber) {
              console.log("[Realtime] Updating form - rev:", newRev, "isRevision:", newIsRevision, "jobNumber:", newJobNumber);
              return {
                ...prev,
                rev: newRev ?? 0,
                isRevision: newIsRevision ?? false,
                jobNumber: newJobNumber ?? prev.jobNumber,
              };
            }
            return prev;
          });

          // Keep lastSavedData in sync
          setLastSavedData((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              rev: newRev ?? 0,
              isRevision: newIsRevision ?? false,
              jobNumber: newJobNumber,
            };
          });
        }
      )
      .subscribe((status) => {
        console.log("[Realtime] Subscription status:", status);
      });

    // Cleanup subscription when modal closes or project changes
    return () => {
      console.log("[Realtime] Cleaning up subscription");
      supabase.removeChannel(channel);
    };
  }, [project?.id, isOpen]);

  // Save job number to DB when it changes (triggers auto_link_revision)
  useEffect(() => {
    const saveJobNumber = async () => {
      if (!project?.id || !isOpen) return;

      const jobNumber = debouncedJobNumber?.trim() || "";

      // If job number is empty or less than 4 digits, save empty/null
      // and reset REV to 0 and is_revision to false
      if (jobNumber.length < 4) {
        if (lastSentJobNumber.current !== null && lastSentJobNumber.current !== "") {
          // Job number was cleared - explicitly reset rev and is_revision
          // (The trigger skips when job_number is empty, so we must set these explicitly)
          console.log("[JobNumber] Clearing job number - resetting rev and is_revision");
          isSavingJobNumber.current = true;
          try {
            await ProjectService.updateProjectBatch(project.id, {
              job_number: null,
              rev: 0,
              is_revision: false,
              parent_project_id: null,
            });
            lastSentJobNumber.current = null;
          } finally {
            isSavingJobNumber.current = false;
          }
        }
        // Reset local display
        setFormData((prev) => (prev.rev !== 0 || prev.isRevision ? { ...prev, rev: 0, isRevision: false } : prev));
        return;
      }

      // Skip if we already sent this exact job number
      if (jobNumber === lastSentJobNumber.current) {
        return;
      }

      console.log("[JobNumber] Saving:", jobNumber, "(was:", lastSentJobNumber.current, ")");
      isSavingJobNumber.current = true;

      try {
        // Save job_number — for revisions, set rev=NULL so auto_link_revision trigger recalculates.
        // For base projects, keep rev=0 since the trigger only fires on parent_project_id changes.
        await ProjectService.updateProjectBatch(project.id, {
          job_number: jobNumber,
          rev: formData.isRevision ? null : 0,
        });
        lastSentJobNumber.current = jobNumber;

        // Immediately fetch the updated values as backup (don't rely solely on realtime)
        // Small delay to ensure trigger has completed
        await new Promise(resolve => setTimeout(resolve, 100));

        const { data: updatedProject, error: fetchError } = await supabase
          .from("projects")
          .select("rev, is_revision")
          .eq("id", project.id)
          .single();

        if (!fetchError && updatedProject) {
          const newRev = updatedProject.rev ?? 0;
          const newIsRevision = updatedProject.is_revision ?? false;
          console.log("[JobNumber] Fetched after save - rev:", newRev, "is_revision:", newIsRevision);

          setFormData((prev) => ({
            ...prev,
            rev: newRev,
            isRevision: newIsRevision,
          }));

          setLastSavedData((prev) => prev ? {
            ...prev,
            rev: newRev,
            isRevision: newIsRevision,
            jobNumber: jobNumber,
          } : prev);
        }
      } catch (err) {
        console.error("[JobNumber] Error saving:", err);
      } finally {
        isSavingJobNumber.current = false;
      }
    };

    saveJobNumber();
  }, [debouncedJobNumber, project?.id, isOpen]);

  // Initialize lastSentJobNumber when modal opens
  useEffect(() => {
    if (project?.jobNumber && isOpen) {
      lastSentJobNumber.current = project.jobNumber;
    }
  }, [project?.jobNumber, isOpen]);

  const autoSave = async () => {
    setAutoSaveStatus("saving");

    try {
      // Use the new numbered format directly - no conversion back to legacy format
      // NOTE: Do NOT include job_number, rev, or is_revision here - those are managed by:
      // 1. job_number: assigned by DB trigger when project_setup_complete becomes true
      // 2. rev/is_revision: managed by auto_link_revision trigger
      // Including them here would overwrite the trigger's assignments!
      const updateData = {
        name: formData.projectName,
        // job_number intentionally omitted - managed by DB trigger
        client_id: formData.clientId || null,
        contact_id: formData.contactId || null,
        us_state: formData.usState || null,
        approximate_acreage: formData.approximateAcreage
          ? parseFloat(formData.approximateAcreage)
          : null,
        template_filter: formData.templateFilter, // Save new numbered format
        additional_requests: formData.additionalRequests,
        pdfs_received: formData.pdfsReceived,
        cad_files_received: formData.cadFilesReceived,
        // project_setup_complete intentionally omitted - only set by handleCompleteSetup
        file_attachments: formData.fileAttachments,
      };

      await ProjectService.updateProjectBatch(project.id, updateData);

      setLastSavedData({ ...formData });
      setAutoSaveStatus("saved");

      // Don't refresh parent on every auto-save - only when modal closes
      // This prevents constant reloading of all projects
    } catch (err) {
      console.error("Auto-save error:", err);
      setAutoSaveStatus("error");
    }
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Create a Set for selected templates for O(1) lookup
  const selectedSet = useMemo(
    () => new Set(formData.templateFilter),
    [formData.templateFilter],
  );

  // Toggle template selection
  const toggleTemplates = (template) => {
    const newSelection = selectedSet.has(template)
      ? formData.templateFilter.filter((t) => t !== template)
      : sortByMaster([...formData.templateFilter, template]);
    handleInputChange("templateFilter", newSelection);
  };

  // Toggle additional requests
  const toggleAdditionalRequest = (request) => {
    const newRequests = formData.additionalRequests.includes(request)
      ? formData.additionalRequests.filter((r) => r !== request)
      : [...formData.additionalRequests, request];
    handleInputChange("additionalRequests", newRequests);
  };

  // Handle file download
  const handleFileDownload = async (url, filename) => {
    if (!url) {
      console.error("No URL provided for download");
      return;
    }

    try {
      // Open the file in a new tab
      window.open(url, "_blank");
    } catch (error) {
      console.error("Error downloading file:", error);
      setError("Failed to download file");
    }
  };

  // Handle base project selection for revisions
  const handleBaseProjectSelect = async (e) => {
    const projectId = e.target.value ? parseInt(e.target.value, 10) : null;
    const baseProject = e.project; // Full project data from BaseProjectSelect

    setSelectedBaseProjectId(projectId);
    setSelectedBaseProject(baseProject);

    if (baseProject) {
      // Auto-fill contact from base project (editable)
      if (baseProject.contactId && !formData.contactId) {
        handleInputChange("contactId", baseProject.contactId);
      }

      // Auto-fill project type from base project
      if (baseProject.projectTypeId) {
        setProjectTypeId(baseProject.projectTypeId.toString());
      }

      // Trigger revision workflow by setting parent_project_id
      // Database trigger (auto_link_revision) fires when parent_project_id changes
      // and automatically sets: job_number, rev, is_revision, slack_channel_id, us_state
      try {
        console.log("[Revision] Setting parent_project_id:", projectId);
        await ProjectService.updateProjectBatch(project.id, {
          parent_project_id: projectId,
        });
        // The realtime subscription will update formData when the trigger completes
      } catch (err) {
        console.error("Error triggering revision workflow:", err);
        setError("Failed to link revision to base project");
      }
    } else {
      // Clear parent_project_id and revision data if no base project selected
      try {
        await ProjectService.updateProjectBatch(project.id, {
          parent_project_id: null,
          is_revision: false,
          rev: 0,
          job_number: null,
        });
      } catch (err) {
        console.error("Error clearing parent project:", err);
      }
    }
  };

  // Handle "Convert to Revision" button click
  const handleConvertToRevision = () => {
    // If setup is already complete, show confirmation since job number will change
    if (formData.projectSetupComplete) {
      setShowConvertToRevisionConfirm(true);
    } else {
      setRevisionMode("converting");
    }
  };

  // Handle "This is NOT a revision" - clear revision state
  const handleNotARevision = async () => {
    setRevisionMode("none");
    setSelectedBaseProjectId(null);
    setSelectedBaseProject(null);
    setShowNotRevisionConfirm(false);

    // Update database to clear revision flags
    try {
      await ProjectService.updateProjectBatch(project.id, {
        is_revision: false,
        parent_project_id: null,
      });
    } catch (err) {
      console.error("Error clearing revision status:", err);
    }
  };

  // Handle canceling "Convert to Revision"
  const handleCancelConversion = () => {
    setRevisionMode("none");
    setSelectedBaseProjectId(null);
    setSelectedBaseProject(null);
  };

  // Handle create slack channel (for revisions only)
  const handleCreateSlackChannel = async () => {
    if (!formData.jobNumber || !formData.projectName) {
      setError("Job number and project name are required");
      return;
    }

    setSlackLoading(true);
    setError(null);

    try {
      // TEMPORARY: Skip Slack channel creation for testing - REMOVE BEFORE PRODUCTION
      await ProjectService.updateProjectBatch(project.id, {
        // slack_channel_id: "pending",
        send_to_billing: "Send to Billing",
      });

      if (onSave) {
        await onSave();
      }
    } catch (err) {
      console.error("Error creating Slack channel:", err);
      setError(err.message || "Failed to create Slack channel");
    } finally {
      setSlackLoading(false);
    }
  };

  // Handle create subitems (for revisions only)
  const handleCreateSubitems = async () => {
    if (!formData.jobNumber || !formData.projectName) {
      setError("Job number and project name are required");
      return;
    }

    setSubitemsLoading(true);
    setError(null);

    try {
      await ProjectService.updateProjectBatch(project.id, {
        create_subitems_new: true,
      });

      if (onSave) {
        await onSave();
      }
    } catch (err) {
      console.error("Error creating subitems:", err);
      setError(err.message || "Failed to create subitems");
    } finally {
      setSubitemsLoading(false);
    }
  };

  // For form_marked revisions, hide other sections until base project is selected
  const hideOtherSectionsForRevision = revisionMode === "form_marked" && !selectedBaseProjectId;

  // Handle complete setup - behavior differs based on revision status
  const handleCompleteSetup = async () => {
    // Determine if this is a revision setup
    // Either: already has rev > 0 OR user selected a base project
    const isExistingRevision = formData.rev && formData.rev !== 0;
    const isNewRevision = selectedBaseProjectId && (revisionMode === "form_marked" || revisionMode === "converting");
    const isRevision = isExistingRevision || isNewRevision;

    // Stricter validation
    const missingFields = [];
    if (!formData.clientId) missingFields.push("Client");
    if (!formData.contactId) missingFields.push("Contact");
    if (!formData.usState) missingFields.push("Project State");

    // For revisions being linked, require base project selection
    if (isNewRevision && !selectedBaseProjectId) {
      missingFields.push("Base Project");
    }

    // For new projects (not revisions), additional requirements
    if (!isRevision) {
      // Require project type for surface design projects
      if (deliverableType === "surface" && !projectTypeId) {
        missingFields.push("Project Type");
      }

      // Require acreage for certain project types
      if (acreageRequired && !formData.approximateAcreage) {
        missingFields.push("Approximate Acreage");
      }
    }

    if (missingFields.length > 0) {
      setError(`Required: ${missingFields.join(", ")}`);
      return;
    }

    setCompleteSetupLoading(true);
    setError(null);

    // Post-setup revision conversion: only link to base project, skip subitem/billing triggers
    const isPostSetupConversion = formData.projectSetupComplete && isNewRevision;

    try {
      const updateData = {};

      if (!isPostSetupConversion) {
        updateData.project_setup_complete = true;
      }

      // Handle revision linking (job number is assigned by DB trigger for new projects)
      if (isNewRevision && selectedBaseProject) {
        // Revision: set parent_project_id so auto_link_revision trigger
        // inherits job_number, rev, slack_channel, etc. from parent
        updateData.parent_project_id = selectedBaseProjectId;
        updateData.is_revision = true;
        updateData.rev = null; // Force trigger to calculate next rev
        console.log("Complete Setup - Linking revision to base project:", selectedBaseProjectId);
      }

      if (!isPostSetupConversion) {
        // Create subitems for both base projects and revisions
        // The database function handles revision-specific template filtering
        updateData.create_subitems_new = true;

        // Only add these triggers for base projects (not revisions)
        if (!isRevision) {
          updateData.setup_triggered = true;
          updateData.send_to_billing = "Send to Billing";
        }

        // Set project_type_id and is_linework_only on the project
        updateData.project_type_id = projectTypeId ? parseInt(projectTypeId, 10) : null;
        updateData.is_linework_only = deliverableType === "linework";

        // Save category assignments FIRST (before create_subitems_new triggers)
        const categoryIds = buildCategoryIds();
        console.log("Complete Setup - saving category IDs:", categoryIds);
        await TemplateCategoryService.updateProjectCategories(project.id, categoryIds);
        console.log("Complete Setup - category assignments saved");
      }

      // NOW update the project (create_subitems_new trigger will find the categories)
      console.log("Complete Setup - updating project:", project.id, "modelingStatus:", project.modelingStatus, "with data:", updateData);
      await ProjectService.updateProjectBatch(project.id, updateData);
      console.log("Complete Setup - update successful");

      // Update local state - job_number will arrive via realtime subscription
      // (assigned by DB trigger, not the frontend)
      handleInputChange("projectSetupComplete", true);

      if (onSave) {
        await onSave();
      }

      // Close the modal automatically after completing setup
      handleModalClose();
    } catch (err) {
      console.error("Error completing setup:", err);
      setError(err.message || "Failed to complete setup");
    } finally {
      setCompleteSetupLoading(false);
    }
  };

  // Add-on key → category name mapping (matches buildCategoryIds logic)
  const addOnCategoryMap = {
    hasPond: { categoryName: "Has Pond", templateFilter: "02-Pond", displayName: "Has Pond" },
    elevatedUtilities: { categoryName: "Elevated Utilities", templateFilter: "02-Elevated Utilities", displayName: "Elevated Utilities" },
    advancedUtilityModel: { categoryName: "Advanced Utility Model", templateFilter: "01-Advanced Utility Model", displayName: "Advanced Utility Model" },
    hasErosionSurface: { categoryName: "Erosion Surface", templateFilter: "02-Erosion Surface", displayName: "Has Erosion Surface" },
    hasCenterlineProfiles: { categoryName: "Has Centerline Profiles", templateFilter: null, displayName: "Has Centerline Profiles" },
  };

  // Handle add-on toggle with confirmation for setup-complete projects
  const handleAddOnToggle = async (addOnKey, newValue, extraStateUpdates = {}) => {
    // If setup not complete, just toggle directly (initial setup flow)
    if (!formData.projectSetupComplete) {
      setAddOns({ ...addOns, [addOnKey]: newValue, ...extraStateUpdates });
      return;
    }

    // Setup is complete — show confirmation dialog
    const mapping = addOnCategoryMap[addOnKey];
    const category = categories.find(c => c.name === mapping.categoryName);

    if (newValue && category) {
      // Adding: fetch templates to show in confirmation
      try {
        const templates = await TemplateCategoryService.getTemplatesByCategory(category.id);
        setPendingCategoryChange({
          addOnKey,
          newValue: true,
          categoryId: category.id,
          categoryName: mapping.displayName,
          templateFilterValue: mapping.templateFilter,
          templates,
          extraStateUpdates,
        });
        setShowCategoryConfirm(true);
      } catch (err) {
        console.error("Error fetching templates for category:", err);
        setError("Failed to load template information");
      }
    } else {
      // Removing: show warning
      setPendingCategoryChange({
        addOnKey,
        newValue: false,
        categoryId: category?.id,
        categoryName: mapping.displayName,
        templateFilterValue: mapping.templateFilter,
        templates: null,
        extraStateUpdates,
      });
      setShowCategoryConfirm(true);
    }
  };

  // Handle category confirmation (add or remove)
  const handleCategoryConfirm = async () => {
    if (!pendingCategoryChange) return;
    setCategoryConfirmLoading(true);

    try {
      const { addOnKey, newValue, categoryId, templateFilterValue, extraStateUpdates } = pendingCategoryChange;

      // Update local addOns state
      setAddOns({ ...addOns, [addOnKey]: newValue, ...extraStateUpdates });

      if (newValue) {
        // ADDING a category
        // 1. Add category assignment
        if (categoryId) {
          await TemplateCategoryService.addProjectCategory(project.id, categoryId);
        }

        // 2. Update template_filter on the project
        if (templateFilterValue && !formData.templateFilter.includes(templateFilterValue)) {
          const updatedFilter = [...formData.templateFilter, templateFilterValue];
          handleInputChange("templateFilter", updatedFilter);

          // 3. Trigger subitem creation (NOT EXISTS in DB prevents duplicates)
          await ProjectService.updateProjectBatch(project.id, {
            template_filter: updatedFilter,
            create_subitems_new: true,
          });
        } else {
          // Still trigger subitem creation even if filter was already there
          await ProjectService.updateProjectBatch(project.id, {
            create_subitems_new: true,
          });
        }
      } else {
        // REMOVING a category
        if (categoryId) {
          await TemplateCategoryService.removeProjectCategory(project.id, categoryId);
        }

        // Remove from template_filter
        if (templateFilterValue) {
          const updatedFilter = formData.templateFilter.filter(t => t !== templateFilterValue);
          handleInputChange("templateFilter", updatedFilter);

          await ProjectService.updateProjectBatch(project.id, {
            template_filter: updatedFilter,
          });
        }
      }

      // Refresh parent
      if (onSave) {
        await onSave();
      }
    } catch (err) {
      console.error("Error updating category:", err);
      setError(err.message || "Failed to update category");
    } finally {
      setCategoryConfirmLoading(false);
      setShowCategoryConfirm(false);
      setPendingCategoryChange(null);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      setLoading(true);
      if (onDelete) {
        await onDelete(project.id);
      }
      setShowDeleteConfirm(false);
      onClose();
    } catch (err) {
      console.error("Error deleting project:", err);
      setError(err.message || "Failed to delete project");
    } finally {
      setLoading(false);
    }
  };

  const handleHoldSuccess = async () => {
    // Refresh the project data after putting it on hold
    if (onSave) {
      await onSave();
    }
  };

  const handleModalClose = async () => {
    // Refresh parent component to show any auto-saved changes
    if (onSave) {
      await onSave();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleModalClose} size="lg">
        {/* Standard Project Modal Header */}
        <div
          style={{
            padding: "var(--space-6)",
            borderBottom: "1px solid var(--gray-700)",
            background:
              "linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))",
            borderTopLeftRadius: "var(--radius-xl)",
            borderTopRightRadius: "var(--radius-xl)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-4)",
            }}
          >
            {/* Job Number and Project Name on one line - Standard for project modals */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: "var(--space-3)",
                }}
              >
                <span
                  style={{
                    fontSize: "var(--text-2xl)",
                    fontWeight: "var(--font-semibold)",
                    color: "var(--gray-400)",
                    flexShrink: 0,
                  }}
                >
                  {formatJobRevNumber(formData.jobNumber, formData.rev) ||
                    "New Project"}
                </span>
                <h1
                  style={{
                    margin: 0,
                    fontSize: "var(--text-2xl)",
                    fontWeight: "var(--font-semibold)",
                    color: "var(--gray-50)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {formData.projectName || "Project Setup"}
                </h1>
              </div>

              {/* Subtitle */}
              <p
                style={{
                  margin: 0,
                  marginTop: "var(--space-2)",
                  fontSize: "var(--text-sm)",
                  color: "var(--gray-400)",
                }}
              >
                Project Setup
              </p>
            </div>
          </div>
        </div>

        <ModalBody>
          {error && (
            <div
              style={{
                padding: "var(--space-3)",
                marginBottom: "var(--space-4)",
                backgroundColor: "rgba(239, 68, 68, 0.1)",
                border: "1px solid var(--danger)",
                borderRadius: "var(--radius-md)",
                color: "var(--danger)",
                fontSize: "var(--text-sm)",
                display: "flex",
                alignItems: "center",
                gap: "var(--space-2)",
              }}
            >
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-6)",
            }}
          >
            {/* Basic Information Section */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: "var(--space-4)",
                  fontSize: "var(--text-lg)",
                  fontWeight: "var(--font-semibold)",
                  color: "var(--gray-200)",
                }}
              >
                Basic Information
              </h2>

              {/* Project Name with Convert to Revision button */}
              <div style={{
                marginBottom: "var(--space-4)",
                display: "flex",
                alignItems: "flex-end",
                gap: "var(--space-3)",
              }}>
                <div style={{ flex: 1 }}>
                  <Input
                    label="Project Name"
                    value={formData.projectName}
                    onChange={(e) =>
                      handleInputChange("projectName", e.target.value)
                    }
                    placeholder="Enter project name"
                    required
                  />
                </div>

                {/* Convert to Revision button - shown when NOT a revision and no revision mode active */}
                {revisionMode === "none" && (parseInt(project?.rev, 10) || 0) === 0 && (
                  <button
                    type="button"
                    onClick={handleConvertToRevision}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--space-2)",
                      padding: "var(--space-2) var(--space-3)",
                      backgroundColor: "rgba(239, 68, 68, 0.1)",
                      border: "1px solid var(--danger)",
                      borderRadius: "var(--radius-md)",
                      color: "var(--danger)",
                      fontSize: "var(--text-sm)",
                      fontWeight: "var(--font-medium)",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      whiteSpace: "nowrap",
                      height: "38px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "rgba(239, 68, 68, 0.2)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "rgba(239, 68, 68, 0.1)";
                    }}
                  >
                    <GitBranch size={14} />
                    Convert to Revision
                  </button>
                )}

                {/* Revision badge for already-linked revisions */}
                {(parseInt(project?.rev, 10) || 0) > 0 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--space-2)",
                      padding: "var(--space-2) var(--space-3)",
                      backgroundColor: "rgba(34, 197, 94, 0.1)",
                      border: "1px solid var(--success)",
                      borderRadius: "var(--radius-md)",
                      height: "38px",
                    }}
                  >
                    <GitBranch size={14} style={{ color: "var(--success)" }} />
                    <span style={{ fontSize: "var(--text-sm)", color: "var(--success)", fontWeight: "var(--font-medium)" }}>
                      Revision #{formData.rev}
                    </span>
                  </div>
                )}
              </div>


              {/* Revision Section - shown when form marked as revision or user is converting */}
              {(revisionMode === "form_marked" || revisionMode === "converting") && (
                <div
                  style={{
                    padding: "var(--space-4)",
                    backgroundColor: revisionMode === "form_marked"
                      ? "rgba(251, 191, 36, 0.1)"
                      : "rgba(59, 130, 246, 0.1)",
                    border: `1px solid ${revisionMode === "form_marked" ? "var(--warning)" : "var(--cta-blue)"}`,
                    borderRadius: "var(--radius-md)",
                    marginBottom: "var(--space-4)",
                  }}
                >
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "var(--space-3)",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                      <GitBranch size={18} style={{ color: revisionMode === "form_marked" ? "var(--warning)" : "var(--cta-blue)" }} />
                      <span style={{
                        fontWeight: "var(--font-semibold)",
                        color: revisionMode === "form_marked" ? "var(--warning)" : "var(--cta-blue)",
                      }}>
                        {revisionMode === "form_marked"
                          ? "Customer Marked as Revision"
                          : "Converting to Revision"}
                      </span>
                    </div>
                    {revisionMode === "converting" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCancelConversion}
                      >
                        <X size={14} style={{ marginRight: "var(--space-1)" }} />
                        Cancel
                      </Button>
                    )}
                  </div>

                  <p style={{
                    fontSize: "var(--text-sm)",
                    color: "var(--gray-300)",
                    margin: 0,
                    marginBottom: "var(--space-3)",
                  }}>
                    {revisionMode === "form_marked"
                      ? "Select client, then choose the base project this is a revision of."
                      : "Select the base project to link this as a revision."}
                  </p>

                  {/* Client Selection for Revision - show first */}
                  <div style={{ marginBottom: "var(--space-3)" }}>
                    <Dropdown
                      label={!formData.clientId && project?.modelRequestClient
                        ? `Client (Request: ${project.modelRequestClient})`
                        : "Client"}
                      options={clientOptions}
                      value={formData.clientId}
                      onChange={(value) => {
                        handleInputChange("clientId", value);
                        // Clear contact and base project when client changes
                        handleInputChange("contactId", "");
                        setSelectedBaseProjectId(null);
                        setSelectedBaseProject(null);
                      }}
                      placeholder="Search for client..."
                      searchable
                    />
                  </div>

                  {/* Base Project Dropdown - filtered by selected client */}
                  <div style={{ marginBottom: "var(--space-3)" }}>
                    <label style={{
                      display: "block",
                      fontSize: "var(--text-sm)",
                      color: "var(--gray-300)",
                      marginBottom: "var(--space-2)",
                    }}>
                      Base Project
                    </label>
                    <BaseProjectSelect
                      clientId={formData.clientId}
                      value={selectedBaseProjectId}
                      onChange={handleBaseProjectSelect}
                      placeholder={formData.clientId ? "Search for base project..." : "Select client first"}
                      disabled={!formData.clientId}
                    />
                  </div>

                  {/* "This is NOT a revision" option */}
                  {revisionMode === "form_marked" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowNotRevisionConfirm(true)}
                      style={{ color: "var(--gray-400)" }}
                    >
                      This is NOT a revision
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Client and Contact Section - hidden for revision mode until base project selected */}
            {!hideOtherSectionsForRevision && (
              <div>
                <h2
                  style={{
                    margin: 0,
                    marginBottom: "var(--space-4)",
                    fontSize: "var(--text-lg)",
                    fontWeight: "var(--font-semibold)",
                    color: "var(--gray-200)",
                  }}
                >
                  {(revisionMode === "form_marked" || revisionMode === "converting")
                    ? "Contact Information"
                    : "Client Information"}
                </h2>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: (revisionMode === "form_marked" || revisionMode === "converting") ? "1fr" : "1fr 1fr",
                    gap: "var(--space-4)",
                  }}
                >
                  {/* Client Dropdown - hide in revision mode since it's shown above */}
                  {!(revisionMode === "form_marked" || revisionMode === "converting") && (
                    <div>
                      <Dropdown
                        label={!formData.clientId && project?.modelRequestClient ? `Client (Request: ${project.modelRequestClient})` : "Client"}
                        options={clientOptions}
                        value={formData.clientId}
                        onChange={(value) => {
                          handleInputChange("clientId", value);
                          // Clear contact when client changes
                          handleInputChange("contactId", "");
                        }}
                        placeholder="Search for client..."
                        searchable
                      />
                    </div>
                  )}

                  {/* Contact Dropdown */}
                  <div>
                    <Dropdown
                      label={!formData.contactId && project?.modelRequestContact ? `Contact (Request: ${project.modelRequestContact})` : "Contact"}
                      options={contactOptions}
                      value={formData.contactId}
                      onChange={(value) => handleInputChange("contactId", value)}
                      placeholder={
                        formData.clientId
                          ? "Select contact..."
                          : "Select client first..."
                      }
                      disabled={!formData.clientId}
                    />
                  </div>

                  {/* State Dropdown */}
                  <div style={{ gridColumn: "1 / -1" }}>
                    <Dropdown
                      label="Project State (Location)"
                      options={usStateOptions}
                      value={formData.usState}
                      onChange={(value) => handleInputChange("usState", value)}
                      placeholder="Select state..."
                      searchable
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Project Details Section - hidden for revision mode until base project selected */}
            {!hideOtherSectionsForRevision && (
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: "var(--space-4)",
                  fontSize: "var(--text-lg)",
                  fontWeight: "var(--font-semibold)",
                  color: "var(--gray-200)",
                }}
              >
                Project Details
              </h2>

              {/* Files Received (left) + Acreage/Date (right) - 2x2 Grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "var(--space-3)",
                  marginBottom: "var(--space-4)",
                }}
              >
                {/* Left Column: PDFs + CAD */}
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
                  {/* PDFs Received Toggle */}
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      padding: "var(--space-2) var(--space-3)",
                      backgroundColor: "var(--dark-elevated)",
                      border: `1px solid ${formData.pdfsReceived ? "var(--cta-blue)" : "var(--gray-700)"}`,
                      borderRadius: "var(--radius-md)",
                      transition: "all 0.15s ease",
                      height: "38px",
                    }}
                    onMouseEnter={(e) => {
                      if (!formData.pdfsReceived) {
                        e.currentTarget.style.borderColor = "var(--gray-600)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!formData.pdfsReceived) {
                        e.currentTarget.style.borderColor = "var(--gray-700)";
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.pdfsReceived}
                      onChange={(e) =>
                        handleInputChange("pdfsReceived", e.target.checked)
                      }
                      style={{ marginRight: "var(--space-2)" }}
                    />
                    <span
                      style={{
                        fontSize: "var(--text-sm)",
                        fontWeight: "var(--font-medium)",
                        color: formData.pdfsReceived
                          ? "var(--cta-blue)"
                          : "var(--gray-300)",
                      }}
                    >
                      PDFs Received
                    </span>
                    {project?.pdfsReceivedDate && (
                      <span
                        style={{
                          fontSize: "var(--text-xs)",
                          color: "var(--gray-400)",
                          marginLeft: "var(--space-2)",
                        }}
                      >
                        ({formatDate(project.pdfsReceivedDate)})
                      </span>
                    )}
                  </label>

                  {/* CAD Files Received Toggle */}
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      padding: "var(--space-2) var(--space-3)",
                      backgroundColor: "var(--dark-elevated)",
                      border: `1px solid ${formData.cadFilesReceived ? "var(--cta-blue)" : "var(--gray-700)"}`,
                      borderRadius: "var(--radius-md)",
                      transition: "all 0.15s ease",
                      height: "38px",
                    }}
                    onMouseEnter={(e) => {
                      if (!formData.cadFilesReceived) {
                        e.currentTarget.style.borderColor = "var(--gray-600)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!formData.cadFilesReceived) {
                        e.currentTarget.style.borderColor = "var(--gray-700)";
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.cadFilesReceived}
                      onChange={(e) =>
                        handleInputChange(
                          "cadFilesReceived",
                          e.target.checked,
                        )
                      }
                      style={{ marginRight: "var(--space-2)" }}
                    />
                    <span
                      style={{
                        fontSize: "var(--text-sm)",
                        fontWeight: "var(--font-medium)",
                        color: formData.cadFilesReceived
                          ? "var(--cta-blue)"
                          : "var(--gray-300)",
                      }}
                    >
                      CAD Files Received
                    </span>
                    {project?.cadFilesReceivedDate && (
                      <span
                        style={{
                          fontSize: "var(--text-xs)",
                          color: "var(--gray-400)",
                          marginLeft: "var(--space-2)",
                        }}
                      >
                        ({formatDate(project.cadFilesReceivedDate)})
                      </span>
                    )}
                  </label>
                </div>

                {/* Right Column: Initial Acceptance */}
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
                  {/* Initial Acceptance Date */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--space-3)",
                      height: "38px",
                    }}
                  >
                    <label
                      style={{
                        fontSize: "var(--text-sm)",
                        fontWeight: "var(--font-medium)",
                        color: "var(--gray-300)",
                        whiteSpace: "nowrap",
                        width: "130px",
                      }}
                    >
                      Initial Acceptance
                    </label>
                    <div
                      style={{
                        flex: 1,
                        padding: "var(--space-2) var(--space-3)",
                        backgroundColor: "var(--dark-elevated)",
                        border: "1px solid var(--gray-700)",
                        borderRadius: "var(--radius-md)",
                        color: "var(--gray-200)",
                        fontSize: "var(--text-sm)",
                        height: "38px",
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {project?.initialAcceptance
                        ? formatDate(project.initialAcceptance)
                        : "Not set"}
                    </div>
                  </div>
                </div>
              </div>

            </div>
            )}

            {/* Model Request Additional Info - always visible */}
            <div style={{ marginBottom: "var(--space-4)" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "var(--space-2)",
                  fontSize: "var(--text-sm)",
                  fontWeight: "var(--font-medium)",
                  color: "var(--gray-300)",
                }}
              >
                Model Request Additional Info
              </label>
              <div
                style={{
                  padding: "var(--space-3)",
                  backgroundColor: "var(--dark-elevated)",
                  border: "1px solid var(--gray-700)",
                  borderRadius: "var(--radius-md)",
                  color: "var(--gray-200)",
                  fontSize: "var(--text-sm)",
                  minHeight: "80px",
                  maxHeight: "120px",
                  overflowY: "auto",
                  whiteSpace: "pre-wrap",
                }}
              >
                {project?.modelRequestAdditionalInfo ||
                  "No additional information provided"}
              </div>
            </div>

            {/* File Attachments - always visible - Side by Side Layout */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "var(--space-2)",
                  fontSize: "var(--text-sm)",
                  fontWeight: "var(--font-medium)",
                  color: "var(--gray-300)",
                }}
              >
                File Attachments
                {formData.fileAttachments?.length > 0 &&
                  ` (${formData.fileAttachments.length})`}
              </label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "var(--space-4)",
                  alignItems: "start",
                }}
              >
                {/* Left: Upload Area */}
                <FileUpload
                  files={formData.fileAttachments || []}
                  onFilesChange={(files) =>
                    handleInputChange("fileAttachments", files)
                  }
                  projectId={project?.id}
                  clientId={project?.clientId}
                  disabled={loading || completeSetupLoading}
                  maxFiles={50}
                  showFileList={false}
                />

                {/* Right: Uploaded Files List */}
                <div
                  style={{
                    backgroundColor: "var(--dark-elevated)",
                    border: "1px solid var(--gray-700)",
                    borderRadius: "var(--radius-md)",
                    padding: "var(--space-3)",
                    maxHeight: "200px",
                    overflowY: "auto",
                  }}
                >
                  {formData.fileAttachments?.length > 0 ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "var(--space-2)",
                      }}
                    >
                      {formData.fileAttachments.map((file) => (
                        <div
                          key={file.id || file.url || file.path}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "var(--space-2)",
                            backgroundColor: "rgba(0, 0, 0, 0.2)",
                            borderRadius: "var(--radius-sm)",
                            fontSize: "var(--text-sm)",
                          }}
                        >
                          <span
                            style={{
                              color: "var(--gray-200)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              flex: 1,
                              marginRight: "var(--space-2)",
                            }}
                            title={file.filename || file.original_filename}
                          >
                            {file.filename || file.original_filename}
                          </span>
                          <div style={{ display: "flex", gap: "var(--space-2)", flexShrink: 0 }}>
                            {(file.url || file.direct_download_url || file.public_url || file.signed_url) && (
                              <a
                                href={file.url || file.direct_download_url || file.public_url || file.signed_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: "var(--cta-blue)",
                                  fontSize: "var(--text-xs)",
                                  textDecoration: "none",
                                }}
                              >
                                View
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      style={{
                        color: "var(--gray-500)",
                        fontSize: "var(--text-sm)",
                        textAlign: "center",
                        padding: "var(--space-4)",
                      }}
                    >
                      No files uploaded
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Template Filter Section - hidden for revision mode until base project selected */}
            {!hideOtherSectionsForRevision && (
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "var(--space-3)",
                  fontSize: "var(--text-lg)",
                  fontWeight: "var(--font-semibold)",
                  color: "var(--gray-200)",
                }}
              >
                Template Filter
              </label>

              {/* Deliverable Type Toggle */}
              <div
                style={{
                  marginBottom: "var(--space-3)",
                  padding: "var(--space-3)",
                  backgroundColor: deliverableType === "linework" ? "rgba(239, 68, 68, 0.05)" : "var(--dark-elevated)",
                  border: `1px solid ${deliverableType === "linework" ? "var(--danger)" : "var(--gray-700)"}`,
                  borderRadius: "var(--radius-md)",
                }}
              >
                <label
                  style={{
                    display: "block",
                    marginBottom: "var(--space-2)",
                    fontSize: "var(--text-sm)",
                    fontWeight: "var(--font-medium)",
                    color: "var(--gray-300)",
                  }}
                >
                  Deliverable Type
                </label>
                <div style={{ display: "flex", gap: "var(--space-2)" }}>
                  <button
                    type="button"
                    onClick={() => setDeliverableType("surface")}
                    style={{
                      flex: 1,
                      padding: "var(--space-2) var(--space-3)",
                      border: `1px solid ${deliverableType === "surface" ? "var(--cta-blue)" : "var(--gray-600)"}`,
                      borderRadius: "var(--radius-md)",
                      backgroundColor: deliverableType === "surface" ? "rgba(59, 130, 246, 0.15)" : "transparent",
                      color: deliverableType === "surface" ? "var(--cta-blue)" : "var(--gray-300)",
                      fontSize: "var(--text-sm)",
                      fontWeight: deliverableType === "surface" ? "var(--font-semibold)" : "normal",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                  >
                    Surface Design
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeliverableType("linework")}
                    style={{
                      flex: 1,
                      padding: "var(--space-2) var(--space-3)",
                      border: `1px solid ${deliverableType === "linework" ? "var(--danger)" : "var(--gray-600)"}`,
                      borderRadius: "var(--radius-md)",
                      backgroundColor: deliverableType === "linework" ? "rgba(239, 68, 68, 0.15)" : "transparent",
                      color: deliverableType === "linework" ? "var(--danger)" : "var(--gray-300)",
                      fontSize: "var(--text-sm)",
                      fontWeight: deliverableType === "linework" ? "var(--font-semibold)" : "normal",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                  >
                    Linework Only
                  </button>
                </div>
                {/* Linework Only Warning */}
                {deliverableType === "linework" && (
                  <div
                    style={{
                      marginTop: "var(--space-3)",
                      padding: "var(--space-3)",
                      backgroundColor: "rgba(239, 68, 68, 0.1)",
                      border: "1px solid var(--danger)",
                      borderRadius: "var(--radius-md)",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "var(--space-2)",
                    }}
                  >
                    <AlertCircle size={16} style={{ color: "var(--danger)", flexShrink: 0, marginTop: 2 }} />
                    <span style={{ fontSize: "var(--text-sm)", color: "var(--danger)" }}>
                      No surface will be modeled for this project. Please confirm the client intended to receive a linework-only model.
                    </span>
                  </div>
                )}
              </div>

              {/* Project Type Dropdown - Only show for Surface Design */}
              {deliverableType === "surface" && (
                <div
                  style={{
                    marginBottom: "var(--space-3)",
                    padding: "var(--space-3)",
                    backgroundColor: "var(--dark-elevated)",
                    border: "1px solid var(--gray-700)",
                    borderRadius: "var(--radius-md)",
                  }}
                >
                  <Dropdown
                    label="Project Type"
                    value={projectTypeId}
                    options={projectTypeOptions}
                    onChange={setProjectTypeId}
                    placeholder="Select project type..."
                    size="md"
                  />

                  {/* Approximate Acreage - Only show when required for this project type */}
                  {acreageRequired && (
                    <div style={{ marginTop: "var(--space-3)" }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "var(--space-2)",
                          fontSize: "var(--text-sm)",
                          fontWeight: "var(--font-medium)",
                          color: "var(--gray-300)",
                        }}
                      >
                        Approximate Acreage <span style={{ color: "var(--danger)" }}>*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.approximateAcreage}
                        onChange={(e) =>
                          handleInputChange("approximateAcreage", e.target.value)
                        }
                        placeholder="Enter acreage..."
                        style={{
                          width: "100%",
                          padding: "var(--space-2) var(--space-3)",
                          backgroundColor: "var(--dark-bg)",
                          border: `1px solid ${!formData.approximateAcreage ? "var(--warning)" : "var(--gray-600)"}`,
                          borderRadius: "var(--radius-md)",
                          color: "var(--gray-200)",
                          fontSize: "var(--text-sm)",
                          height: "38px",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Add-ons and File Formats - Side by Side */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "var(--space-4)",
                  marginBottom: "var(--space-3)",
                }}
              >
                {/* Left: Add-ons Checkboxes */}
                <div
                  style={{
                    padding: "var(--space-3)",
                    backgroundColor: "var(--dark-elevated)",
                    border: "1px solid var(--gray-700)",
                    borderRadius: "var(--radius-md)",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      marginBottom: "var(--space-2)",
                      fontSize: "var(--text-sm)",
                      fontWeight: "var(--font-medium)",
                      color: "var(--gray-300)",
                    }}
                  >
                    Add-ons
                  </label>
                  <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                    {/* Project Has a Pond - only show if project type is not Pond */}
                    {projectTypeId !== categoriesGrouped.projectType.find(c => c.name === "Pond")?.id.toString() && (
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "var(--space-2)",
                          padding: "var(--space-2)",
                          backgroundColor: addOns.hasPond ? "rgba(59, 130, 246, 0.1)" : "transparent",
                          border: `1px solid ${addOns.hasPond ? "var(--cta-blue)" : "var(--gray-600)"}`,
                          borderRadius: "var(--radius-md)",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={addOns.hasPond}
                          onChange={(e) => handleAddOnToggle("hasPond", e.target.checked)}
                        />
                        <span style={{ fontSize: "var(--text-sm)", color: "var(--gray-200)" }}>
                          Project Has a Pond
                        </span>
                        <span style={{ fontSize: "var(--text-xs)", color: "var(--warning)", fontStyle: "italic" }}>
                          *verify for each project
                        </span>
                      </label>
                    )}

                    {/* Elevated Utilities */}
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "var(--space-2)",
                        padding: "var(--space-2)",
                        backgroundColor: addOns.elevatedUtilities ? "rgba(59, 130, 246, 0.1)" : "transparent",
                        border: `1px solid ${addOns.elevatedUtilities ? "var(--cta-blue)" : "var(--gray-600)"}`,
                        borderRadius: "var(--radius-md)",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={addOns.elevatedUtilities}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          handleAddOnToggle("elevatedUtilities", checked, checked ? {} : { advancedUtilityModel: false });
                        }}
                      />
                      <span style={{ fontSize: "var(--text-sm)", color: "var(--gray-200)" }}>
                        Elevated Utilities (3D Pipe)
                      </span>
                    </label>

                    {/* Advanced Utility Model - only show when Elevated Utilities is checked */}
                    {addOns.elevatedUtilities && (
                      <>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "var(--space-2)",
                            padding: "var(--space-2)",
                            marginLeft: "var(--space-4)",
                            backgroundColor: addOns.advancedUtilityModel ? "rgba(251, 191, 36, 0.1)" : "transparent",
                            border: `1px solid ${addOns.advancedUtilityModel ? "var(--warning)" : "var(--gray-600)"}`,
                            borderRadius: "var(--radius-md)",
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={addOns.advancedUtilityModel}
                            onChange={(e) => handleAddOnToggle("advancedUtilityModel", e.target.checked)}
                          />
                          <span style={{ fontSize: "var(--text-sm)", color: addOns.advancedUtilityModel ? "var(--warning)" : "var(--gray-200)" }}>
                            Advanced Utility Model
                          </span>
                        </label>
                        {addOns.advancedUtilityModel && (
                          <div
                            style={{
                              marginLeft: "var(--space-4)",
                              padding: "var(--space-2)",
                              fontSize: "var(--text-xs)",
                              color: "var(--warning)",
                              backgroundColor: "rgba(251, 191, 36, 0.1)",
                              borderRadius: "var(--radius-sm)",
                              border: "1px solid var(--warning)",
                            }}
                          >
                            <AlertCircle size={12} style={{ display: "inline", marginRight: "var(--space-1)" }} />
                            Additional setup and pricing required
                          </div>
                        )}
                      </>
                    )}

                    {/* Has Erosion Surface */}
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "var(--space-2)",
                        padding: "var(--space-2)",
                        backgroundColor: addOns.hasErosionSurface ? "rgba(34, 197, 94, 0.1)" : "transparent",
                        border: `1px solid ${addOns.hasErosionSurface ? "var(--success)" : "var(--gray-600)"}`,
                        borderRadius: "var(--radius-md)",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={addOns.hasErosionSurface}
                        onChange={(e) => handleAddOnToggle("hasErosionSurface", e.target.checked)}
                      />
                      <span style={{ fontSize: "var(--text-sm)", color: addOns.hasErosionSurface ? "var(--success)" : "var(--gray-200)" }}>
                        Has Erosion Surface
                      </span>
                    </label>

                    {/* Erosion Surface State Warning */}
                    {addOns.hasErosionSurface && formData.usState && !["VA", "NC", "SC", "AL", "GA"].includes(formData.usState) && (
                      <div
                        style={{
                          padding: "var(--space-2)",
                          fontSize: "var(--text-xs)",
                          color: "var(--warning)",
                          backgroundColor: "rgba(251, 191, 36, 0.1)",
                          borderRadius: "var(--radius-sm)",
                          border: "1px solid var(--warning)",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "var(--space-1)",
                        }}
                      >
                        <AlertCircle size={12} style={{ flexShrink: 0, marginTop: 2 }} />
                        <span>Please confirm this project has a separate erosion design (state: {formData.usState})</span>
                      </div>
                    )}

                    {/* Has Centerline Profiles - Only show for Subdivision (Corridor always has it) */}
                    {projectTypeId && categoriesGrouped.projectType.find(c => c.id.toString() === projectTypeId)?.name === "Subdivision" && (
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "var(--space-2)",
                          padding: "var(--space-2)",
                          backgroundColor: addOns.hasCenterlineProfiles ? "rgba(59, 130, 246, 0.1)" : "transparent",
                          border: `1px solid ${addOns.hasCenterlineProfiles ? "var(--cta-blue)" : "var(--gray-600)"}`,
                          borderRadius: "var(--radius-md)",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={addOns.hasCenterlineProfiles}
                          onChange={(e) => handleAddOnToggle("hasCenterlineProfiles", e.target.checked)}
                        />
                        <span style={{ fontSize: "var(--text-sm)", color: "var(--gray-200)" }}>
                          Has Centerline Profiles
                        </span>
                        <span style={{ fontSize: "var(--text-xs)", color: "var(--warning)", fontStyle: "italic" }}>
                          *verify for each project
                        </span>
                      </label>
                    )}
                  </div>
                </div>

                {/* Right: File Formats (locked with edit option) */}
                <div
                  style={{
                    padding: "var(--space-3)",
                    backgroundColor: "var(--dark-elevated)",
                    border: "1px solid var(--gray-700)",
                    borderRadius: "var(--radius-md)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "var(--space-2)",
                    }}
                  >
                    <label
                      style={{
                        fontSize: "var(--text-sm)",
                        fontWeight: "var(--font-medium)",
                        color: "var(--gray-300)",
                      }}
                    >
                      File Formats Needed
                    </label>
                    {!isEditingFileFormats ? (
                      <button
                        type="button"
                        onClick={() => {
                          setEditedFileFormats(project?.fileFormatsNeeded || []);
                          setIsEditingFileFormats(true);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "var(--space-1)",
                          padding: "var(--space-1) var(--space-2)",
                          backgroundColor: "transparent",
                          border: "none",
                          borderRadius: "var(--radius-sm)",
                          color: "var(--gray-400)",
                          fontSize: "var(--text-xs)",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = "var(--gray-200)"}
                        onMouseLeave={(e) => e.currentTarget.style.color = "var(--gray-400)"}
                      >
                        <Pencil size={12} />
                        Edit
                      </button>
                    ) : (
                      <div style={{ display: "flex", gap: "var(--space-2)" }}>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await ProjectService.updateProjectBatch(project.id, {
                                file_formats_needed: editedFileFormats,
                              });
                              setIsEditingFileFormats(false);
                              if (onSave) await onSave();
                            } catch (err) {
                              console.error("Error saving file formats:", err);
                              setError("Failed to save file formats");
                            }
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "var(--space-1)",
                            padding: "var(--space-1) var(--space-2)",
                            backgroundColor: "var(--success)",
                            border: "none",
                            borderRadius: "var(--radius-sm)",
                            color: "white",
                            fontSize: "var(--text-xs)",
                            cursor: "pointer",
                          }}
                        >
                          <Check size={12} />
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditingFileFormats(false);
                            setEditedFileFormats([]);
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "var(--space-1)",
                            padding: "var(--space-1) var(--space-2)",
                            backgroundColor: "transparent",
                            border: "1px solid var(--gray-600)",
                            borderRadius: "var(--radius-sm)",
                            color: "var(--gray-400)",
                            fontSize: "var(--text-xs)",
                            cursor: "pointer",
                          }}
                        >
                          <X size={12} />
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                    {/* File format checkboxes */}
                    {["Trimble", "Topcon", "Leica"].map((format) => {
                      const isChecked = isEditingFileFormats
                        ? editedFileFormats.includes(format)
                        : project?.fileFormatsNeeded?.includes(format);
                      return (
                        <label
                          key={format}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "var(--space-2)",
                            padding: "var(--space-2)",
                            backgroundColor: isChecked ? "rgba(59, 130, 246, 0.1)" : "transparent",
                            border: `1px solid ${isChecked ? "var(--cta-blue)" : "var(--gray-700)"}`,
                            borderRadius: "var(--radius-md)",
                            cursor: isEditingFileFormats ? "pointer" : "not-allowed",
                            opacity: isEditingFileFormats ? 1 : 0.7,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={!isEditingFileFormats}
                            onChange={(e) => {
                              if (isEditingFileFormats) {
                                if (e.target.checked) {
                                  setEditedFileFormats([...editedFileFormats, format]);
                                } else {
                                  setEditedFileFormats(editedFileFormats.filter(f => f !== format));
                                }
                              }
                            }}
                            style={{ cursor: isEditingFileFormats ? "pointer" : "not-allowed" }}
                          />
                          <span style={{ fontSize: "var(--text-sm)", color: isChecked ? "var(--gray-200)" : "var(--gray-500)" }}>
                            {format}
                          </span>
                        </label>
                      );
                    })}
                    {/* International Feet - sourced from project_categories (DB trigger), not templateFilter */}
                    {hasInternationalFeet && (
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "var(--space-2)",
                          padding: "var(--space-2)",
                          backgroundColor: "rgba(59, 130, 246, 0.1)",
                          border: "1px solid var(--cta-blue)",
                          borderRadius: "var(--radius-md)",
                          cursor: "not-allowed",
                          opacity: 0.7,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={true}
                          disabled
                          style={{ cursor: "not-allowed" }}
                        />
                        <span style={{ fontSize: "var(--text-sm)", color: "var(--gray-200)" }}>
                          International Feet
                        </span>
                      </label>
                    )}
                    {!isEditingFileFormats && (
                      <div
                        style={{
                          fontSize: "var(--text-xs)",
                          color: "var(--gray-500)",
                          fontStyle: "italic",
                          marginTop: "var(--space-1)",
                        }}
                      >
                        From model request form
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Project Setup Complete Toggle - only show if already complete */}
              {formData.projectSetupComplete && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--space-3)",
                    padding: "var(--space-3)",
                    backgroundColor: "rgba(34, 197, 94, 0.1)",
                    border: "1px solid var(--success)",
                    borderRadius: "var(--radius-md)",
                  }}
                >
                  <span
                    style={{
                      fontSize: "var(--text-sm)",
                      color: "var(--success)",
                      fontWeight: "var(--font-medium)",
                      flex: 1,
                    }}
                  >
                    Project Setup Complete
                  </span>
                  <label
                    style={{
                      position: "relative",
                      display: "inline-block",
                      width: "44px",
                      height: "24px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.projectSetupComplete}
                      readOnly
                      style={{
                        opacity: 0,
                        width: 0,
                        height: 0,
                      }}
                    />
                    <span
                      style={{
                        position: "absolute",
                        cursor: "pointer",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "var(--success)",
                        transition: "all 0.2s ease",
                        borderRadius: "24px",
                      }}
                    >
                      <span
                        style={{
                          position: "absolute",
                          height: "18px",
                          width: "18px",
                          left: "23px",
                          bottom: "3px",
                          backgroundColor: "white",
                          transition: "all 0.2s ease",
                          borderRadius: "50%",
                        }}
                      />
                    </span>
                  </label>
                </div>
              )}
            </div>
            )}
          </div>
        </ModalBody>

        <ModalFooter>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              gap: "var(--space-3)",
            }}
          >
            {/* Left section - Delete button */}
            <div style={{ flexShrink: 0 }}>
              {onDelete && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={loading}
                >
                  <Trash2 size={14} style={{ marginRight: "var(--space-2)" }} />
                  Delete
                </Button>
              )}
            </div>

            {/* Right section - Action buttons */}
            <div style={{ display: "flex", gap: "var(--space-3)", alignItems: "center" }}>
              {/* Show Create Chat Channel button for revisions only */}
              {formData.rev && formData.rev !== 0 ? (
                <Button
                  variant={project?.slackChannelId ? "success" : "secondary"}
                  onClick={handleCreateSlackChannel}
                  disabled={slackLoading || project?.slackChannelId}
                >
                  {project?.slackChannelId ? (
                    <>
                      <Check
                        size={16}
                        style={{ marginRight: "var(--space-2)" }}
                      />
                      Channel Created
                    </>
                  ) : (
                    <>
                      <MessageSquare
                        size={16}
                        style={{ marginRight: "var(--space-2)" }}
                      />
                      {slackLoading ? "Creating..." : "Create Chat Channel"}
                    </>
                  )}
                </Button>
              ) : null}

              {/* Create Subitems button - shown for all projects */}
              <Button
                variant={project?.createSubitems ? "success" : "secondary"}
                onClick={handleCreateSubitems}
                disabled={subitemsLoading || project?.createSubitems}
              >
                {project?.createSubitems ? (
                  <>
                    <Check
                      size={16}
                      style={{ marginRight: "var(--space-2)" }}
                    />
                    Subitems Created
                  </>
                ) : (
                  <>
                    <List
                      size={16}
                      style={{ marginRight: "var(--space-2)" }}
                    />
                    {subitemsLoading ? "Creating..." : "Create Subitems"}
                  </>
                )}
              </Button>

              {/* Complete Setup button - always shown but behavior differs */}
              <Button
                variant={formData.projectSetupComplete ? "success" : "primary"}
                onClick={handleCompleteSetup}
                disabled={
                  completeSetupLoading ||
                  // Allow re-clicking if converting to revision post-setup
                  (formData.projectSetupComplete && revisionMode !== "converting") ||
                  // Always require client and contact
                  !formData.clientId ||
                  !formData.contactId ||
                  // If revision mode is active, require base project selection
                  ((revisionMode === "form_marked" || revisionMode === "converting") && !selectedBaseProjectId)
                }
                title={
                  !formData.clientId || !formData.contactId
                    ? "Required: Client and Contact"
                    : (revisionMode === "form_marked" || revisionMode === "converting") && !selectedBaseProjectId
                      ? "Required: Select Base Project"
                      : ""
                }
              >
                {formData.projectSetupComplete && revisionMode !== "converting" ? (
                  <>
                    <CheckCircle2
                      size={16}
                      style={{ marginRight: "var(--space-2)" }}
                    />
                    Setup Complete
                  </>
                ) : revisionMode === "converting" && formData.projectSetupComplete ? (
                  <>
                    <GitBranch
                      size={16}
                      style={{ marginRight: "var(--space-2)" }}
                    />
                    Link as Revision
                  </>
                ) : (
                  <>
                    <CheckCircle2
                      size={16}
                      style={{ marginRight: "var(--space-2)" }}
                    />
                    {completeSetupLoading ? "Completing..." : "Complete Setup"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>

      {/* Hold Project Modal */}
      <HoldProjectModal
        project={project}
        isOpen={showHoldModal}
        onClose={() => setShowHoldModal(false)}
        onSuccess={handleHoldSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Project"
        message={`Are you sure you want to delete "${formData.projectName || "this project"}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={loading}
      />

      {/* Treat as New Project Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showRevisionConfirm}
        onClose={() => setShowRevisionConfirm(false)}
        onConfirm={() => {
          handleInputChange("isRevision", false);
          setShowRevisionConfirm(false);
        }}
        title="Treat Revision as New Project?"
        message="This will create subitems as if this were a new base project instead of a revision. This is rarely needed - only use if this revision needs the full set of new project subitems."
        confirmText="Yes, Treat as New"
        cancelText="Cancel"
        variant="warning"
      />

      {/* Not a Revision Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showNotRevisionConfirm}
        onClose={() => setShowNotRevisionConfirm(false)}
        onConfirm={handleNotARevision}
        title="Mark as NOT a Revision?"
        message="This will treat this project as a new base project. A new job number will be assigned when setup is completed."
        confirmText="Yes, Not a Revision"
        cancelText="Cancel"
        variant="warning"
      />

      {/* Convert to Revision (post-setup) Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showConvertToRevisionConfirm}
        onClose={() => setShowConvertToRevisionConfirm(false)}
        onConfirm={() => {
          setShowConvertToRevisionConfirm(false);
          setRevisionMode("converting");
        }}
        title="Convert to Revision?"
        message={`This project's setup is already complete${formData.jobNumber ? ` with job number ${formData.jobNumber}` : ''}. Converting to a revision will change the job number to match the base project and assign a revision number.`}
        confirmText="Yes, Convert to Revision"
        cancelText="Cancel"
        variant="warning"
      />

      {/* Category Add/Remove Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showCategoryConfirm}
        onClose={() => {
          setShowCategoryConfirm(false);
          setPendingCategoryChange(null);
        }}
        onConfirm={handleCategoryConfirm}
        title={pendingCategoryChange?.newValue
          ? `Add ${pendingCategoryChange?.categoryName}?`
          : `Remove ${pendingCategoryChange?.categoryName}?`
        }
        message={pendingCategoryChange?.newValue ? (
          <div>
            <p style={{ margin: "0 0 var(--space-3) 0" }}>
              The following subitems will be created for this project:
            </p>
            <ul style={{
              margin: "0 0 var(--space-3) 0",
              paddingLeft: "var(--space-5)",
              listStyleType: "disc",
            }}>
              {pendingCategoryChange?.templates?.map((t) => (
                <li key={t.id} style={{ marginBottom: "var(--space-1)", fontSize: "var(--text-sm)" }}>
                  {t.name} <span style={{ color: "var(--gray-400)" }}>({t.phase})</span>
                </li>
              ))}
            </ul>
            <p style={{ margin: 0, fontSize: "var(--text-sm)", color: "var(--gray-400)" }}>
              Existing subitems will not be duplicated.
            </p>
          </div>
        ) : (
          <div>
            <p style={{ margin: 0 }}>
              This will remove the <strong>{pendingCategoryChange?.categoryName}</strong> category
              from this project. Existing subitems from this category will <strong>not</strong> be
              automatically deleted — you can remove them manually if needed.
            </p>
          </div>
        )}
        confirmText={pendingCategoryChange?.newValue ? "Add & Create Subitems" : "Remove Category"}
        cancelText="Cancel"
        variant={pendingCategoryChange?.newValue ? "info" : "warning"}
        loading={categoryConfirmLoading}
      />
    </>
  );
};

export default ProjectSetupModal;
